import { createApi } from '@reduxjs/toolkit/query/react'
import axios from 'axios'
import { Endpoints } from 'src/common/constants'
import { trackPurchase, trackRegistration, trackUser } from 'src/common/lib/segment'
import { Receipt } from 'src/services/types/joinEventV2'
import { AppState } from 'src/store'
import {
    resetAthleteData,
    updateAthleteAchievements,
    updateAthleteData
} from 'src/store/athleteProfile'
import {
    checkoutComplete,
    syncJoinEventState,
    throwError,
    updateSalesTax,
    updateVouchers
} from 'src/store/joinevent'
import { prepareCartObject } from 'src/store/joinevent/actions'
import { resetUserData, updateUserData, updateUserReferralCode } from 'src/store/user'
import { EVENT_TYPE } from '../common/constants/eventTypes'
import { axiosBaseQuery } from './baseQuery'
import { Athlete } from './types/athlete'
import { ChallengeDiscoveryRes } from './types/challengeDiscovery'
import { GetAllEventsRes } from './types/getAllEvents'
import { AthleteAchievementsRes } from './types/getAthleteAchievements'
import { GetUserEventsRes, UserEventsRes } from './types/getUserEvents'
import { JoinEventRes } from './types/joinEventV2'
import { IFacebookLogin, IRegisterBody } from './types/register'
import { Challenge, SearchEventsRes } from './types/searchEvents'
import { ILoginUser } from './types/user'

/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
// import typehole from 'typehole'

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ResetPWResponse {
    newPassword: string
    sendEmail: boolean
}

type ValidatePromotionsArgs = {
    amount: any
    challengeIds: any[]
    userId: any
}

interface ValidateVouchersArgs extends ValidatePromotionsArgs {
    code: string
    existingVouchers: any[]
}

interface ReferralCodeResp {
    hasError: true
    message: string
    error: string
    voucherifyReferralCode: string
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface DiscoverLaneRes {
    lanes: Lane[]
    hasError: boolean
    message: null
    error: null
}
interface Lane {
    laneId: number
    isWeb: boolean
    isNative: boolean
    isActive: boolean
    name: string
    description: string
    position: number
    createTS: string
    createUserId: number
    eventTypeId: number
    challengeLanes: ChallengeLane[]
    isDirty: boolean
    isDelete: boolean
    key: string
}
interface ChallengeLane {
    challengeLaneId: number
    challengeId: number
    laneId: number
    position: number
    createTS: string
    createUserId: number
    challenge: ChallengeInLane
    isDirty: boolean
    isDelete: boolean
    key: string
    isHowFarCanYouGo: boolean
    totalDistance: number
    distanceUnit: string
}
interface ChallengeInLane {
    minimumPrice: any
    skillLevelString: any
    distanceUnit: any
    totalDistance: any
    duration: number
    challengeId: number
    name: string
    challengeUrl: string
    price: number
    isPrivateChallenge: boolean
    startDate: null | string | string
    endDate: null
    registrationCloseDate: null | null | string
    sortWeight: number
    isExercise: boolean
    isSwag: boolean
    is30Day: boolean
    isHowFarCanYouGo: boolean
    logo: null | string | string
    key: number
    eventType: number
}

type TrackProgressBody = { progressSeconds: number; challengeImageId: number }

type StatsBody = { userId: number; isPrivate: boolean; isFitnessPrivate: boolean }
// eslint-disable-next-line @typescript-eslint/no-empty-interface

// Define a service using a base URL and expected endpoints
const mainApi = createApi({
    reducerPath: 'mainApi',
    baseQuery: axiosBaseQuery({
        baseUrl: process.env.NEXT_PUBLIC_API_HOST
    }),
    tagTypes: ['Challenge'],
    endpoints: (builder) => ({
        signIn: builder.mutation<ILoginUser, { email: string; password: string }>({
            async queryFn(body, { dispatch }, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: Endpoints.LOGIN_API,
                    method: 'GET',
                    urlParams: {
                        username: body.email,
                        password: body.password
                    }
                })

                if (res.error) {
                    return { error: res.error }
                }

                const data = res.data as ILoginUser

                dispatch(updateUserData(data))

                return { data }
            }
        }),
        refresh: builder.mutation<ILoginUser, void>({
            async queryFn(body, { dispatch }) {
                const res = await axios.get(
                    `${process.env.NEXT_PUBLIC_API_HOST}/${Endpoints.REFRESH_API}`,
                    {
                        headers: {
                            Accept: 'application/json; charset=utf-8',
                            Authorization: `Bearer ${String(body)}`
                        }
                    }
                )

                if (res.status !== 200 || res.data?.hasError) {
                    return { error: 'We could not log you in.' }
                }

                const data = res.data as ILoginUser
                dispatch(updateUserData(data))

                return { data }
            }
        }),
        register: builder.mutation<any, IRegisterBody>({
            async queryFn(body, { dispatch }, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: Endpoints.LOGIN_API,
                    method: 'POST',
                    data: body
                })

                if (res.error) {
                    return { error: res.error }
                }

                const data = res.data as ILoginUser

                trackUser(data)
                trackRegistration(data)
                dispatch(updateUserData(data))

                return { data }
            }
        }),
        facebookLogin: builder.mutation<any, IFacebookLogin>({
            async queryFn(body, { dispatch }, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: Endpoints.LOGIN_API,
                    method: 'POST',
                    data: body
                })

                if (res.error) {
                    return { error: res.error }
                }

                const data = res.data as ILoginUser

                dispatch(updateUserData(data))

                return { data }
            }
        }),
        getAthlete: builder.query<any, string>({
            async queryFn(userName, { dispatch }, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: `${Endpoints.ATHLETE_API}/${userName}`,
                    method: 'GET'
                })

                if (!res.data) {
                    dispatch(resetAthleteData())
                    dispatch(resetUserData())
                    if (typeof window !== 'undefined') {
                        localStorage.clear()
                    }
                    return { error: res.error }
                }

                const data = res.data as Athlete

                dispatch(updateAthleteData(data))
                return { data: data }
            }
        }),
        getUserProfile: builder.query<any, string>({
            async queryFn(userName, _, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    url: `${Endpoints.USER_SUMMARY_API}/${userName}`,
                    method: 'GET'
                })
                console.log({ res })

                if (!res.data) {
                    return { error: 'Athlete not found.' }
                }
                const data = res.data as Athlete
                console.log('🚀 ~ file: mainAPI.ts ~ line 251 ~ queryFn ~ data', data)
                return { data: data }
            }
        }),
        updateProfileStats: builder.mutation<any, StatsBody>({
            async queryFn(args, _, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    url: `${Endpoints.USER_SUMMARY_API}`,
                    method: 'POST',
                    data: {
                        ...args
                    }
                })

                const data = res.data

                return { data: data }
            }
        }),
        getAthleteAchievements: builder.query<any, string>({
            async queryFn(username, { dispatch }, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: `${Endpoints.ACHIEVEMENTS_API}/${username}`,
                    method: 'GET'
                })

                if (!res.data) {
                    return { error: res.error }
                }

                const data = res.data as AthleteAchievementsRes
                dispatch(updateAthleteAchievements(data))

                return { data: data }
            }
        }),
        getUserEvents: builder.query<UserEventsRes, string>({
            async queryFn(userName, _queryApi, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: `${Endpoints.EVENT_HISTORY_API}/${userName}`,
                    method: 'GET'
                })

                if (res.error) {
                    return { error: res.error }
                }

                const data = res.data as GetUserEventsRes

                let races = []
                let challenges = []

                if (data) {
                    races = data.filter(
                        (item: { is30Day: boolean; isSwag: boolean }) =>
                            item.is30Day === false && item.isSwag === false
                    )

                    challenges = data.filter(
                        (item: { is30Day: boolean }) => item.is30Day === true
                    )
                }

                return { data: { races, challenges } }
            }
        }),
        getAllEvents: builder.query<GetAllEventsRes, null>({
            async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: Endpoints.SEARCH_EVENTS_API,
                    method: 'GET'
                })

                if (res.error) throw res.error

                const data = res.data as SearchEventsRes

                let races = []
                let challenges = []
                let merch = []
                let exercises = []

                if (data) {
                    races = data
                        .filter((item) => item.eventType === EVENT_TYPE.RACES)
                        .slice(0, 14)
                    challenges = data
                        .filter((item) => item.eventType === EVENT_TYPE.CHALLENGE)
                        .slice(0, 14)
                    merch = data
                        .filter((item) => item.eventType === EVENT_TYPE.SWAG)
                        .slice(0, 14)
                    exercises =
                        data
                            ?.filter((item) => item.eventType === EVENT_TYPE.EXERCISES)
                            ?.slice(0, 14) ?? []
                }

                return { data: { races, challenges, merch, exercises }, error: null }
            }
        }),
        getAllRaces: builder.query<Challenge[], null>({
            async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: `${Endpoints.SEARCH_EVENTS_API}`,
                    method: 'GET'
                })

                if (res.error) return { error: res.error }

                const data = res.data as SearchEventsRes

                let races = []

                if (data) {
                    races = data.filter(
                        (item) =>
                            item.eventType === EVENT_TYPE.RACES &&
                            item.challengeAthleteId === null
                    )
                }

                return { data: races, error: null }
            }
        }),
        searchEvents: builder.query<any, string | null>({
            async queryFn(query, _queryApi, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: `${Endpoints.EVENT_LIST_API}/0/0/${query ? query : ''}`,
                    method: 'GET'
                })

                if (res.error) return { error: res.error }

                const data = res.data

                return { data, error: null }
            }
        }),
        getLanes: builder.query<DiscoverLaneRes, number>({
            async queryFn(eventType, _queryApi, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: `${Endpoints.DISCOVER_EVENT_LIST_API}/${eventType}/0`,
                    method: 'GET'
                })

                if (res.error) return { error: res.error }

                const data = res.data as DiscoverLaneRes

                return { data, error: null }
            }
        }),
        getAllChallenges: builder.query<Challenge[], null>({
            async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: Endpoints.SEARCH_EVENTS_API,
                    method: 'GET'
                })

                if (res.error) throw res.error

                const data = res.data as SearchEventsRes

                let challenges = []

                if (data) {
                    challenges = data.filter(
                        (item) => item.eventType === EVENT_TYPE.CHALLENGE
                    )
                }

                return { data: challenges, error: null }
            }
        }),
        getAllExercises: builder.query<Challenge[], null>({
            async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: Endpoints.SEARCH_EVENTS_API,
                    method: 'GET'
                })

                if (res.error) throw res.error

                const data = res.data as SearchEventsRes

                let exercises = []

                if (data) {
                    exercises = data.filter(
                        (item) => item.eventType === EVENT_TYPE.EXERCISES
                    )
                }

                return { data: exercises, error: null }
            }
        }),
        getAllMerch: builder.query<Challenge[], null>({
            async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: Endpoints.SEARCH_EVENTS_API,
                    method: 'GET'
                })

                if (res.error) throw res.error

                const data = res.data as SearchEventsRes

                let merch = []

                if (data) {
                    merch = data.filter((item) => item.eventType === EVENT_TYPE.SWAG)
                }

                return { data: merch, error: null }
            }
        }),
        getExerciseVideo: builder.query<any, number>({
            async queryFn(challengeId, _queryApi, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: `${Endpoints.EXERCISE_DETAIL_API}/${challengeId}`,
                    method: 'GET'
                })

                if (!res.data) throw Error('no video')

                const data = res.data

                return { data, error: null }
            }
        }),
        getEventById: builder.query<JoinEventRes, string>({
            providesTags: (_result, _error, id) => [{ type: 'Challenge', id }],
            async queryFn(id, { dispatch, getState }, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: `${Endpoints.JOIN_CHALLENGE_API}/${id}`,
                    method: 'GET'
                })

                if (res.error) throw res.error

                const data = res.data as JoinEventRes

                const unselectedRewards = data.rewards.map((item) => {
                    return {
                        ...item,
                        selected: false
                    }
                })

                const unselectedAddons = data.addons.map((item) => {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                    return {
                        ...item,
                        selected: false
                    }
                })

                dispatch(
                    syncJoinEventState({
                        old_state: (getState() as AppState).joinevent,
                        addons: unselectedAddons,
                        rewards: unselectedRewards,
                        cart: data.cart,
                        challengeId: data.summary.challengeId,
                        summary: data.summary
                    })
                )
                return { data, error: null }
            },
            keepUnusedDataFor: 0
        }),
        getRelatedRaces: builder.query<any, number>({
            async queryFn(id, _queryApi, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: Endpoints.DISCOVER_API,
                    method: 'POST',
                    data: {
                        challengeId: id,
                        types: [5],
                        limit: 15
                    }
                })

                if (res.error) throw res.error

                const data = res.data as ChallengeDiscoveryRes

                return { data, error: null }
            }
        }),
        getRelatedRacesForUser: builder.query<any, any>({
            async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: Endpoints.DISCOVER_API,
                    method: 'POST',
                    data: {
                        challengeId: null,
                        types: [5],
                        limit: 15,
                        eventType: 1
                    }
                })

                if (res.error) throw res.error

                const data = res.data

                return { data, error: null }
            }
        }),
        getReferralCode: builder.query<any, number>({
            async queryFn(_arg, { dispatch }, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: Endpoints.VOUCHER_API,
                    method: 'GET'
                })

                if (res.error) throw res.error

                const data = (res.data as ReferralCodeResp).voucherifyReferralCode
                dispatch(updateUserReferralCode(data))

                return { data: null, error: null }
            }
        }),
        getRelatedChallenges: builder.query<any, number>({
            async queryFn(id, _queryApi, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: Endpoints.DISCOVER_API,
                    method: 'POST',
                    data: {
                        challengeId: id,
                        types: [5],
                        limit: 15,
                        eventType: 2
                    }
                })

                if (res.error) throw res.error

                const data = res.data

                return { data, error: null }
            }
        }),
        getRelatedExercises: builder.query<any, number>({
            async queryFn(id, _queryApi, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: Endpoints.DISCOVER_API,
                    method: 'POST',
                    data: {
                        challengeId: id,
                        types: [1, 2, 3, 4, 5],
                        limit: 8,
                        eventType: 4
                    }
                })

                if (res.error) throw res.error

                const data = res.data

                return { data, error: null }
            }
        }),
        getStatesData: builder.query<any, null>({
            async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: Endpoints.FETCH_STATES_API,
                    method: 'GET'
                })

                if (res.error) throw res.error

                const data = res.data

                return { data, error: null }
            }
        }),
        validatePromotions: builder.query<any, ValidatePromotionsArgs>({
            async queryFn(body, { getState, dispatch }, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: Endpoints.PROMOTIONS_API,
                    method: 'POST',
                    data: body
                })

                const state = getState() as AppState

                if (res.error) throw res.error

                const data: any = res.data

                compareVoucherState(state, data, dispatch)

                return { data, error: null }
            }
        }),
        validateVoucher: builder.query<any, ValidateVouchersArgs>({
            async queryFn(body, { getState, dispatch }, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: Endpoints.VOUCHER_API,
                    method: 'POST',
                    data: body
                })
                const state = getState() as AppState

                if (res.error) {
                    dispatch(throwError(res.error))
                    throw res.error
                }

                const data: any = res.data

                // compareVoucherState(state, data, dispatch)

                const newVoucherState = [...state.joinevent.cart.vouchers, data.voucher]

                dispatch(updateVouchers(newVoucherState))

                return { data, error: null }
            }
        }),
        getSalesTax: builder.mutation<any, any>({
            async queryFn(body, { dispatch }, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: Endpoints.FETCH_SALES_TAX_API,
                    method: 'POST',
                    data: body
                })

                if (res.error) throw res.error

                const data: any = res.data

                dispatch(updateSalesTax(data))

                return { data, error: null }
            }
        }),
        trackProgress: builder.mutation<any, TrackProgressBody>({
            async queryFn(body, _, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: Endpoints.TRACK_PROGRESS_API,
                    method: 'POST',
                    data: body
                })

                if (res.error) throw res.error

                const data: any = res.data

                return { data, error: null }
            }
        }),
        sendPWResetEmail: builder.mutation<ResetPWResponse, string>({
            async queryFn(email, _, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ({
                    url: Endpoints.PASSWORD_RESET_API,
                    method: 'POST',
                    data: {
                        email,
                        sendEmail: true
                    }
                })

                if (res.error) throw res.error

                const data = res.data as ResetPWResponse

                if (data.newPassword === 'Email not found')
                    return { error: 'Email not found' }

                return { data, error: null }
            }
        }),
        checkoutRequest: builder.mutation<any, null>({
            async queryFn(_body, { getState, dispatch }, _extraOptions, fetchWithBQ) {
                const state = getState() as AppState
                const user = state.user.data
                const cart = prepareCartObject(state)

                const res = await fetchWithBQ({
                    url: Endpoints.JOIN_CHALLENGE_API,
                    method: 'POST',
                    data: cart
                })

                if (res.error) throw res.error

                const data: any = res.data

                const receipt: Receipt = {
                    ...state.joinevent.cart,
                    total: cart.total,
                    finished: true,
                    paid: data.paid,
                    challengeAthleteId: data.challengeAthleteId,
                    charged: data.charged,
                    transactionId: data.transactionId,
                    isPaused: data.isPaused,
                    isNew: data.isNew,
                    challengeId: data.challengeId,
                    athleteId: data.athleteId,
                    userDataAPIId: data.userDataAPIId,
                    waiverAccepted: data.waiverAccepted,
                    validicApp: data.validicApp,
                    activityType: data.activityType,
                    isChallengeComplete: false,
                    bundle: data.bundle,
                    orderID: data.orderID
                }
                dispatch(checkoutComplete({ receipt }))
                trackPurchase(receipt, user)
                return { data: null, error: null }
            }
        })
    })
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetAllEventsQuery,
    useGetAllRacesQuery,
    useGetAllChallengesQuery,
    useGetAllMerchQuery,
    useGetEventByIdQuery,
    useGetAthleteQuery,
    useGetAthleteAchievementsQuery,
    useGetUserEventsQuery,
    useRefreshMutation,
    useSignInMutation,
    useRegisterMutation,
    useFacebookLoginMutation,
    useGetStatesDataQuery,
    useValidateVoucherQuery,
    useCheckoutRequestMutation,
    useGetRelatedRacesQuery,
    useGetExerciseVideoQuery,
    useGetReferralCodeQuery,
    useTrackProgressMutation,
    useSendPWResetEmailMutation,
    useSearchEventsQuery,
    useLazySearchEventsQuery,
    useUpdateProfileStatsMutation,
    useGetUserProfileQuery
} = mainApi

// Possible exports
export const {
    endpoints: {
        getAllEvents,
        getAllRaces,
        getLanes,
        getAllExercises,
        getAllChallenges,
        getAllMerch,
        getEventById,
        signIn,
        getAthlete,
        getUserProfile,
        getAthleteAchievements,
        getUserEvents,
        register,
        facebookLogin,
        getStatesData,
        validatePromotions,
        validateVoucher,
        getSalesTax,
        checkoutRequest,
        getRelatedRaces,
        getRelatedExercises,
        getRelatedChallenges,
        getReferralCode,
        getRelatedRacesForUser
    }
} = mainApi

export default mainApi

function compareVoucherState(state, data: any, dispatch) {
    let newVoucherState = state.joinevent?.cart?.vouchers?.filter(
        (voucher) => !voucher.isPromotion
    )

    if (data.voucher !== null && data.voucher.code !== null) {
        newVoucherState = newVoucherState.concat(data.voucher)
    }

    if (newVoucherState.length) {
        dispatch(updateVouchers(newVoucherState))
    }
}
